import { useCallback, useEffect, useRef, useState } from 'react'
import { ActionIcon, Group, Text } from '@mantine/core'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { templateThumbUrl } from '../utils/uiGroups'
import classes from './ThumbnailGridField.module.css'

/**
 * The visual option picker from the Figma "Layout" card: a row of illustrated cards
 * with the selected one outlined in pink, and ‹ › paging when the options overflow.
 *
 * The heading count ("Types (7)") is rendered from the live option list rather than
 * hardcoded, so it tracks the schema — the Figma frame's own counts were drawn against
 * an older option set and no longer match.
 */
export function ThumbnailGridField({ field, value, options, error, onChange, templateManifest, configuration = null }) {
  const { t } = useTranslation()
  const ui = field.ui || {}
  const cols = Number(ui.cols) || 4
  const items = options || field.options || []
  const releaseKey = templateManifest && templateManifest.releaseKey

  const scroller = useRef(null)
  const [overflow, setOverflow] = useState({ left: false, right: false })

  const measure = useCallback(() => {
    const node = scroller.current
    if (!node) return
    const max = node.scrollWidth - node.clientWidth
    setOverflow({
      left: node.scrollLeft > 4,
      right: max > 4 && node.scrollLeft < max - 4,
    })
  }, [])

  useEffect(() => {
    measure()
    const node = scroller.current
    if (!node) return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [measure, items.length])

  // Keep the active card in view when the value changes from elsewhere (presets, AI).
  useEffect(() => {
    const node = scroller.current
    if (!node) return
    const active = node.querySelector('[data-selected="true"]')
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }
  }, [value])

  const page = (direction) => {
    const node = scroller.current
    if (!node) return
    node.scrollBy({ left: direction * node.clientWidth * 0.9, behavior: 'smooth' })
  }

  const paging = ui.carousel !== false && (overflow.left || overflow.right)
  const heading = ui.heading || field.label

  return (
    <div className={classes.root}>
      <Group justify="space-between" align="center" mb={8} wrap="nowrap">
        <Text className={classes.heading}>
          {heading} ({items.length})
        </Text>
        {paging && (
          <Group gap={6} wrap="nowrap">
            <ActionIcon
              variant="default"
              size="md"
              radius="sm"
              aria-label={t('editorV2.form.common.previousOptions', { title: heading })}
              disabled={!overflow.left}
              onClick={() => page(-1)}
            >
              <IconChevronLeft size={16} />
            </ActionIcon>
            <ActionIcon
              variant="default"
              size="md"
              radius="sm"
              aria-label={t('editorV2.form.common.moreOptions', { title: heading })}
              disabled={!overflow.right}
              onClick={() => page(1)}
            >
              <IconChevronRight size={16} />
            </ActionIcon>
          </Group>
        )}
      </Group>

      <div
        ref={scroller}
        className={classes.track}
        style={{ '--thumb-cols': cols }}
        onScroll={measure}
        role="radiogroup"
        aria-label={heading}
      >
        {items.map((option) => {
          const selected = option.value === value
          const src = templateThumbUrl(releaseKey, ui.thumbBase, option.value, configuration)
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              data-selected={selected ? 'true' : 'false'}
              className={classes.card}
              onClick={() => onChange(option.value)}
            >
              <span className={classes.frame} data-selected={selected ? 'true' : 'false'}>
                {src ? (
                  <img className={classes.thumb} src={src} alt="" loading="lazy" draggable="false" />
                ) : (
                  <span className={classes.thumbFallback} aria-hidden="true" />
                )}
              </span>
              <span className={classes.caption}>{option.label || option.value}</span>
            </button>
          )
        })}
      </div>

      {error && (
        <Text c="red" size="xs" mt={6}>
          {error}
        </Text>
      )}
    </div>
  )
}
